import { Address, Time } from '@/lib/db-client';
import { setCallRecords } from '@/lib/collections/callRecords';
import { setChatMessages } from '@/lib/collections/chatMessages';
import { SOL } from '@/lib/constants';
import type { AgentsResponse } from '@/lib/collections/agents';
import { formatSol } from './format';

/**
 * Every renter gets one persistent chat session per agent, stored locally.
 * This purely groups chatMessages into a thread — payment now happens
 * PER CALL (see payForCall below), not once per session.
 */
export function getOrCreateSessionId(agentId: string, walletAddress: string): string {
  const key = `th_session_${agentId}_${walletAddress}`;
  let sessionId = localStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(key, sessionId);
  }
  return sessionId;
}

export interface PaymentResult {
  success: boolean;
  amountPaid?: number;
  error?: string;
}

/**
 * Pays for exactly ONE agent call: creates the onchain callRecord (the
 * native-SOL split-payment hook transfers creatorAmount to the agent's
 * creator and platformAmount to the platform treasury, both from the
 * renter, atomically), then posts a "Paid X SOL" system chat message so
 * it shows up in the thread right before the agent's response.
 *
 * Called right before every message send / audio upload — every call is
 * individually paid, matching the per-call pricing model.
 */
export async function payForCall(
  agent: AgentsResponse,
  walletAddress: string,
  sessionId: string
): Promise<PaymentResult> {
  const pricePerCall = Math.floor(agent.pricePerCall);
  const creatorAmount = Math.floor((pricePerCall * agent.creatorSplitPct) / 100);
  const platformAmount = Math.floor((pricePerCall * agent.platformSplitPct) / 100);
  const amountPaid = creatorAmount + platformAmount;
  const callId = crypto.randomUUID();

  const paid = await setCallRecords(callId, {
    agentId: agent.id,
    renterWallet: Address.publicKey(walletAddress),
    creatorWallet: Address.publicKey(agent.creatorWallet),
    creatorAmount,
    platformAmount,
    amountPaid,
    tokenMint: SOL,
    timestamp: Time.Now,
  });

  if (!paid) {
    return { success: false, error: 'Payment was declined or failed. Please try again.' };
  }

  await setChatMessages(crypto.randomUUID(), {
    sessionId,
    agentId: agent.id,
    renterWallet: Address.publicKey(walletAddress),
    sender: 'system',
    content: `Paid ${formatSol(amountPaid)}`,
    createdAt: Time.Now,
  });

  return { success: true, amountPaid };
}
